import React, { useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Database,
  BarChart3,
  BookOpen,
  Building2,
  Scale,
  ArrowRight,
  Info,
  ExternalLink
} from 'lucide-react';
import type {
  MissingResearchItem,
  MissingResearchCategory,
  MissingResearchPriority
} from '../../../types/workflowAnalysis';

interface MissingResearchAlertProps {
  missingResearch: MissingResearchItem[];
  researchQualityScore?: number;
  researchQualityNotes?: string;
  className?: string;
}

// Category icon mapping
const CategoryIcon: React.FC<{ category: MissingResearchCategory }> = ({ category }) => {
  const iconClass = "w-5 h-5";

  switch (category) {
    case 'financial_data':
      return <TrendingUp className={`${iconClass} text-green-600`} />;
    case 'market_data':
      return <BarChart3 className={`${iconClass} text-blue-600`} />;
    case 'operational_data':
      return <Database className={`${iconClass} text-purple-600`} />;
    case 'domain_knowledge':
      return <BookOpen className={`${iconClass} text-amber-600`} />;
    case 'company_specific':
      return <Building2 className={`${iconClass} text-cyan-600`} />;
    case 'external_benchmark':
      return <Scale className={`${iconClass} text-indigo-600`} />;
    default:
      return <Info className={`${iconClass} text-neutral-600`} />;
  }
};

// Category display names
const CATEGORY_LABELS: Record<MissingResearchCategory, string> = {
  financial_data: 'Financial Data',
  market_data: 'Market Data',
  operational_data: 'Operational Data',
  domain_knowledge: 'Domain Knowledge',
  company_specific: 'Company-Specific Data',
  external_benchmark: 'External Benchmarks'
};

// Priority badge styling
const PriorityBadge: React.FC<{ priority: MissingResearchPriority }> = ({ priority }) => {
  const styles: Record<MissingResearchPriority, string> = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-blue-100 text-blue-700 border-blue-200'
  };

  const labels: Record<MissingResearchPriority, string> = {
    critical: 'Critical',
    high: 'High Priority',
    medium: 'Medium',
    low: 'Low Priority'
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[priority]}`}>
      {labels[priority]}
    </span>
  );
};

// Quality score indicator
const QualityScoreIndicator: React.FC<{ score: number }> = ({ score }) => {
  const getColor = (score: number) => {
    if (score >= 80) return { bg: 'bg-green-500', text: 'text-green-700', label: 'Good' };
    if (score >= 60) return { bg: 'bg-amber-500', text: 'text-amber-700', label: 'Fair' };
    if (score >= 40) return { bg: 'bg-orange-500', text: 'text-orange-700', label: 'Limited' };
    return { bg: 'bg-red-500', text: 'text-red-700', label: 'Insufficient' };
  };

  const config = getColor(score);

  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-2 bg-neutral-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${config.bg} rounded-full transition-all duration-300`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className={`text-sm font-medium ${config.text}`}>
        {score}% ({config.label})
      </span>
    </div>
  );
};

// Individual missing research item card
const MissingResearchCard: React.FC<{
  item: MissingResearchItem;
  expanded: boolean;
  onToggle: () => void;
}> = ({ item, expanded, onToggle }) => {
  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-neutral-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <CategoryIcon category={item.category} />
          <div className="text-left">
            <p className="font-medium text-neutral-900 text-sm">{item.title}</p>
            <p className="text-xs text-neutral-500">
              {CATEGORY_LABELS[item.category]}
              {item.research_module && ` • ${item.research_module}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PriorityBadge priority={item.priority} />
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-neutral-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-neutral-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="p-4 pt-0 border-t border-neutral-100 space-y-4">
          {/* Description */}
          <div>
            <p className="text-xs font-medium text-neutral-600 mb-1">What's Missing</p>
            <p className="text-sm text-neutral-700">{item.description}</p>
          </div>

          {/* Impact on Analysis */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs font-medium text-amber-800 mb-1">Impact on Analysis</p>
            <p className="text-sm text-amber-700">{item.impact_on_analysis}</p>
          </div>

          {/* Suggested Action */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-blue-800 mb-1">Suggested Action</p>
                <p className="text-sm text-blue-700">{item.suggested_action}</p>
              </div>
            </div>
          </div>

          {/* Specific Data Needed */}
          {item.specific_data_needed && item.specific_data_needed.length > 0 && (
            <div>
              <p className="text-xs font-medium text-neutral-600 mb-2">Specific Data Needed</p>
              <div className="flex flex-wrap gap-1.5">
                {item.specific_data_needed.map((data, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-neutral-100 text-neutral-700 text-xs rounded"
                  >
                    {data}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Example Sources */}
          {item.example_sources && item.example_sources.length > 0 && (
            <div>
              <p className="text-xs font-medium text-neutral-600 mb-2">Example Sources</p>
              <ul className="space-y-1">
                {item.example_sources.map((source, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-neutral-600">
                    <ExternalLink className="w-3 h-3 text-neutral-400" />
                    <span>{source}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const MissingResearchAlert: React.FC<MissingResearchAlertProps> = ({
  missingResearch,
  researchQualityScore,
  researchQualityNotes,
  className = ''
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set([0]));
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!missingResearch || missingResearch.length === 0) {
    return null;
  }

  const toggleItem = (index: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Count by priority
  const criticalCount = missingResearch.filter(
    item => item.priority === 'critical' || item.priority === 'high'
  ).length;
  const mediumCount = missingResearch.filter(item => item.priority === 'medium').length;
  const lowCount = missingResearch.filter(item => item.priority === 'low').length;

  // Determine alert severity
  const hasCritical = missingResearch.some(
    item => item.priority === 'critical' || item.priority === 'high'
  );

  return (
    <div className={`${className}`}>
      {/* Alert Header */}
      <div
        className={`p-4 rounded-lg border ${
          hasCritical
            ? 'bg-red-50 border-red-200'
            : 'bg-amber-50 border-amber-200'
        }`}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle
            className={`w-6 h-6 ${hasCritical ? 'text-red-600' : 'text-amber-600'}`}
          />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h4
                className={`font-semibold ${
                  hasCritical ? 'text-red-800' : 'text-amber-800'
                }`}
              >
                {hasCritical ? 'Missing Research Data' : 'Research Improvement Opportunities'}
              </h4>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className={`p-1 rounded hover:bg-white/50 ${
                  hasCritical ? 'text-red-600' : 'text-amber-600'
                }`}
              >
                {isCollapsed ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronUp className="w-4 h-4" />
                )}
              </button>
            </div>

            <p
              className={`text-sm mt-1 ${
                hasCritical ? 'text-red-700' : 'text-amber-700'
              }`}
            >
              {hasCritical
                ? 'This analysis would significantly improve with additional research data. Critical items may affect the accuracy of recommendations.'
                : 'Additional research data could enhance the quality of this analysis.'}
            </p>

            {/* Summary Stats */}
            <div className="flex items-center gap-3 mt-3">
              <span className="text-xs font-medium text-neutral-600">
                {missingResearch.length} item{missingResearch.length !== 1 ? 's' : ''} identified:
              </span>
              {criticalCount > 0 && (
                <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                  {criticalCount} critical/high
                </span>
              )}
              {mediumCount > 0 && (
                <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                  {mediumCount} medium
                </span>
              )}
              {lowCount > 0 && (
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                  {lowCount} low
                </span>
              )}
            </div>

            {/* Research Quality Score */}
            {researchQualityScore !== undefined && (
              <div className="mt-3 pt-3 border-t border-neutral-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-neutral-600">
                    Current Research Quality:
                  </span>
                  <QualityScoreIndicator score={researchQualityScore} />
                </div>
                {researchQualityNotes && (
                  <p className="text-xs text-neutral-500 mt-1 italic">
                    {researchQualityNotes}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expandable Details */}
      {!isCollapsed && (
        <div className="mt-4 space-y-3">
          {/* Sort by priority: critical > high > medium > low */}
          {[...missingResearch]
            .sort((a, b) => {
              const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
              return priorityOrder[a.priority] - priorityOrder[b.priority];
            })
            .map((item, index) => (
              <MissingResearchCard
                key={index}
                item={item}
                expanded={expandedItems.has(index)}
                onToggle={() => toggleItem(index)}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default MissingResearchAlert;
